<template>
  <a-form :form="form" layout="vertical" @submit.prevent="submit">
    <div class="form-section">
      <div class="section-title">
        1. {{ $t("decaissement.formSectionGeneral") }}
      </div>
      <div class="row">
        <div class="col-md-4">
          <a-form-item :label="$t('decaissement.expenseMode')">
            <a-radio-group
              button-style="solid"
              v-decorator="[
                'expenseMode',
                {
                  initialValue: 'normal',
                  rules: [{ required: true, message: $t('decaissement.validation.expenseModeRequired') }],
                },
              ]"
              @change="onExpenseModeChange"
            >
              <a-radio-button value="normal">
                {{ $t("decaissement.normal") }}
              </a-radio-button>
              <a-radio-button value="declaree">
                {{ $t("decaissement.declaree") }}
              </a-radio-button>
            </a-radio-group>
          </a-form-item>
        </div>
        <div class="col-md-4">
          <a-form-item :label="$t('bulletin.label')">
            <a-input
              v-decorator="[
                'label',
                {
                  rules: [
                    {
                      required: true,
                      whitespace: true,
                      message: $t('requis.designation'),
                    },
                  ],
                },
              ]"
            />
          </a-form-item>
        </div>
        <div class="col-md-4">
          <a-form-item :label="$t('comptabilite.reference')">
            <a-input v-decorator="['reference', { initialValue: '' }]" />
          </a-form-item>
        </div>
      </div>

      <div class="row">
        <div class="col-md-4">
          <a-form-item :label="$t('comptabilite.tabUnites')">
            <a-select
              v-decorator="['unit']"
              allow-clear
              show-search
              option-filter-prop="children"
            >
              <a-select-option
                v-for="u in lookups.units"
                :key="u._id"
                :value="u._id"
              >
                {{ u.name }}
              </a-select-option>
            </a-select>
          </a-form-item>
        </div>
        <div class="col-md-4">
          <a-form-item :label="$t('etablissement.anneeScolaire')">
            <a-select v-decorator="['schoolarYear']">
              <a-select-option
                v-for="y in sortedSchoolarYears"
                :key="y._id"
                :value="y._id"
              >
                {{ y.name }}
              </a-select-option>
            </a-select>
          </a-form-item>
        </div>
        <div class="col-md-4">
          <a-form-item :label="$t('decaissement.depenseCreatedAt')">
            <a-date-picker
              v-decorator="[
                'entryDate',
                {
                  rules: [
                    { required: true, message: $t('decaissement.validation.entryDateRequired') },
                    entryDateRule,
                  ],
                  initialValue: initialDateMoment,
                },
              ]"
              style="width: 100%"
              format="DD/MM/YYYY"
              :disabled-date="disableFutureDates"
            />
          </a-form-item>
        </div>
      </div>
    </div>

    <div class="form-section">
      <div class="section-title">
        2. {{ $t("decaissement.formSectionDetails") }}
      </div>
      <div class="row">
        <div class="col-md-4">
          <a-form-item :label="$t('decaissement.invoiceDate')">
            <a-date-picker
              v-decorator="[
                'date',
                {
                  rules: [
                    { required: true, message: $t('decaissement.validation.invoiceDateRequired') },
                    invoiceDateRule,
                  ],
                  initialValue: initialDateMoment,
                },
              ]"
              style="width: 100%"
              format="DD/MM/YYYY"
              :disabled-date="disableFutureDates"
            />
          </a-form-item>
        </div>
      </div>
      <template v-if="expenseMode === 'normal'">
        <div class="row">
          <div class="col-md-6">
            <a-form-item :label="$t('comptabilite.tabDepenseSubcategories')">
              <a-select
                v-decorator="[
                  'subCategory',
                  {
                    rules: [{ required: true, message: $t('decaissement.validation.subCategoryRequired') }],
                  },
                ]"
                show-search
                :filter-option="subcategoryFilterOption"
              >
                <a-select-opt-group
                  v-for="g in subcategoryGroups"
                  :key="g.categoryId"
                >
                  <span slot="label" class="depense-subcategory-group-label">
                    <span class="depense-subcategory-group-name">{{
                      g.categoryName
                    }}</span>
                    <a-tag
                      v-if="g.expenseKind"
                      :color="expenseKindTagColor(g.expenseKind)"
                      class="depense-subcategory-expense-kind-tag"
                    >
                      {{ $t(expenseKindTagLabelKey(g.expenseKind)) }}
                    </a-tag>
                  </span>
                  <a-select-option
                    v-for="s in g.subs"
                    :key="s._id"
                    :value="s._id"
                  >
                    <a-icon
                      :type="s.direction === 'inverse' ? 'swap' : 'arrow-right'"
                      :style="{
                        color:
                          s.direction === 'inverse' ? '#fa8c16' : '#52c41a',
                        marginRight: '6px',
                      }"
                    />
                    {{ s.name }}
                    <a-tag
                      v-if="s.direction === 'inverse'"
                      color="orange"
                      style="
                        margin-left: 4px;
                        font-size: 10px;
                        line-height: 16px;
                        padding: 0 4px;
                      "
                      >{{ $t("decaissement.directionInverse") }}</a-tag
                    >
                  </a-select-option>
                </a-select-opt-group>
              </a-select>
            </a-form-item>
          </div>
          <div class="col-md-6">
            <a-form-item :label="$t('recette.montant')" :required="true">
              <a-input-number
                v-decorator="[
                  'amount',
                  {
                    rules: [normalAmountRule],
                  },
                ]"
                :min="0"
                style="width: 100%"
                @change="bumpFormTick"
              />
            </a-form-item>
          </div>
        </div>
      </template>

      <template v-else>
        <div class="subsection-title">
          2.1 {{ $t("decaissement.formSubsectionBeneficiary") }}
        </div>
        <div class="row">
          <div class="col-md-4">
            <a-form-item :label="$t('decaissement.beneficiaryType')">
              <a-select
                v-decorator="[
                  'beneficiaryType',
                  {
                    initialValue: 'provider',
                    rules: [{ required: true, message: $t('decaissement.validation.beneficiaryTypeRequired') }],
                  },
                ]"
                @change="onBeneficiaryTypeChange"
              >
                <a-select-option
                  value="provider"
                  :label="$t('factures.beneficiaryProvider')"
                >
                  <beneficiary-type-display type="provider" />
                </a-select-option>
                <a-select-option
                  value="associate"
                  :label="$t('factures.beneficiaryAssociate')"
                >
                  <beneficiary-type-display type="associate" />
                </a-select-option>
                <a-select-option
                  value="employee"
                  :label="$t('factures.beneficiaryEmployee')"
                >
                  <beneficiary-type-display type="employee" />
                </a-select-option>
                <a-select-option
                  value="company"
                  :label="$t('factures.beneficiaryCompany')"
                >
                  <beneficiary-type-display type="company" />
                </a-select-option>
              </a-select>
            </a-form-item>
          </div>
          <div class="col-md-4">
            <a-form-item :label="$t('decaissement.beneficiaryName')">
              <a-select
                v-decorator="[
                  'beneficiaryId',
                  {
                    rules: [{ required: true, message: $t('decaissement.validation.beneficiaryRequired') }],
                  },
                ]"
                show-search
                option-filter-prop="label"
                @change="onBeneficiaryIdChange"
              >
                <a-select-option
                  v-for="b in beneficiaryOptions"
                  :key="b._id"
                  :value="b._id"
                  :label="beneficiaryLabel(b)"
                >
                  <template v-if="beneficiaryType === 'employee'">
                    <i
                      v-if="b.isA_Teacher"
                      class="fa fa-briefcase text-success pr-1"
                      :title="$t('menu.enseignants')"
                    />
                    <i
                      v-else
                      class="fa fa-suitcase text-warning pr-1"
                      :title="$t('personnel.staff')"
                    />
                  </template>
                  {{ beneficiaryLabel(b) }}
                  <template v-if="beneficiaryType === 'provider'">
                    <span class="text-muted">
                      — {{ $t("fournisseur.retenue") }}:
                      {{ formatRetentionPercent(b.retenue) }}%
                    </span>
                  </template>
                </a-select-option>
              </a-select>
              <div v-if="beneficiaryAccountingClass" class="mt-1">
                <a-tag color="geekblue">
                  {{ $t("comptabilite.accountingClass") }}:
                  {{ beneficiaryAccountingClass }}
                </a-tag>
              </div>
            </a-form-item>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <a-form-item :label="$t('comptabilite.tabDepenseSubcategories')">
              <a-select
                v-decorator="[
                  'subCategory',
                  {
                    rules: [{ required: true, message: $t('decaissement.validation.subCategoryRequired') }],
                  },
                ]"
                show-search
                :filter-option="subcategoryFilterOption"
                @change="bumpFormTick"
              >
                <a-select-opt-group
                  v-for="g in subcategoryGroups"
                  :key="g.categoryId"
                >
                  <span slot="label" class="depense-subcategory-group-label">
                    <span class="depense-subcategory-group-name">{{
                      g.categoryName
                    }}</span>
                    <a-tag
                      v-if="g.expenseKind"
                      :color="expenseKindTagColor(g.expenseKind)"
                      class="depense-subcategory-expense-kind-tag"
                    >
                      {{ $t(expenseKindTagLabelKey(g.expenseKind)) }}
                    </a-tag>
                  </span>
                  <a-select-option
                    v-for="s in g.subs"
                    :key="s._id"
                    :value="s._id"
                  >
                    <a-icon
                      :type="s.direction === 'inverse' ? 'swap' : 'arrow-right'"
                      :style="{
                        color:
                          s.direction === 'inverse' ? '#fa8c16' : '#52c41a',
                        marginRight: '6px',
                      }"
                    />
                    {{ s.name }}
                    <a-tag
                      v-if="s.direction === 'inverse'"
                      color="orange"
                      style="
                        margin-left: 4px;
                        font-size: 10px;
                        line-height: 16px;
                        padding: 0 4px;
                      "
                      >{{ $t("decaissement.directionInverse") }}</a-tag
                    >
                  </a-select-option>
                </a-select-opt-group>
              </a-select>
            </a-form-item>
          </div>
        </div>

        <template v-if="beneficiaryType === 'provider'">
          <div class="subsection-title">
            2.2 {{ $t("decaissement.formSubsectionProvider") }}
          </div>
          <div class="row">
            <div class="col-md-3">
              <a-form-item :label="$t('decaissement.invoiceNumber')">
                <a-input
                  v-decorator="[
                    'invoiceNumber',
                    {
                      rules: [
                        {
                          required: true,
                          whitespace: true,
                          message: $t('decaissement.validation.invoiceNumberRequired'),
                        },
                      ],
                    },
                  ]"
                />
              </a-form-item>
            </div>
            <div class="col-md-3">
              <a-form-item :label="$t('invoice.montantHT')" :required="true">
                <a-input-number
                  v-decorator="[
                    'amountHT',
                    {
                      rules: [amountHTRule],
                    },
                  ]"
                  :min="0"
                  style="width: 100%"
                  @change="(v) => onProviderAmountPartChange(v, undefined)"
                />
              </a-form-item>
            </div>

            <div class="col-md-3">
              <a-form-item
                :label="$t('decaissement.amountTVA')"
                :required="true"
              >
                <a-input-number
                  v-decorator="[
                    'amountTVA',
                    {
                      rules: [amountTvaRule],
                    },
                  ]"
                  :min="0"
                  style="width: 100%"
                  @change="(v) => onProviderAmountPartChange(undefined, v)"
                />
              </a-form-item>
            </div>
            <div class="col-md-3">
              <a-form-item
                :label="$t('decaissement.amountTTC')"
                :required="true"
              >
                <a-input-number
                  v-decorator="[
                    'amountTTC',
                    {
                      rules: [amountTTCRule],
                    },
                  ]"
                  :min="0"
                  style="width: 100%"
                  @change="bumpFormTick"
                />
              </a-form-item>
            </div>
          </div>
          <div class="row align-items-end">
            <div class="col-md-6">
              <a-form-item :label="$t('decaissement.amountAfterRetention')">
                <a-input-number
                  :value="computedAmountAfterRetention"
                  style="width: 100%"
                  :disabled="true"
                />
              </a-form-item>
            </div>
            <div class="col-md-6">
              <a-form-item>
                <a-checkbox
                  :key="'apply-ret-' + retentionCheckboxKey"
                  v-decorator="[
                    'applyRetention',
                    {
                      valuePropName: 'checked',
                      initialValue: defaultApplyRetentionForProvider,
                    },
                  ]"
                  :disabled="!providerHasRetention"
                  @change="bumpFormTick"
                >
                  {{ $t("decaissement.applyRetention") }}
                  <span v-if="providerHasRetention" class="text-muted ml-1">
                    ({{ formatRetentionPercent(providerRetentionPercent) }}%)
                  </span>
                </a-checkbox>
              </a-form-item>
            </div>
          </div>
          <div class="row">
            <div class="col-md-12">
              <a-form-item :label="$t('decaissement.levels')">
                <a-select
                  v-decorator="['levels', { initialValue: [] }]"
                  mode="multiple"
                  allow-clear
                  show-search
                  option-filter-prop="children"
                  :placeholder="$t('enseignant.listeNiveaux')"
                  style="width: 100%"
                >
                  <a-select-option
                    v-for="opt in levelOptionsList"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </div>
          </div>

          <p
            v-if="showCapexEligibility && !providerCapexAmountsReady"
            class="section-hint text-muted mb-0"
          >
            {{ $t("decaissement.capexCompleteAmountsFirst") }}
          </p>

          <template v-if="showCapexAmortizationFormSection">
            <div class="subsection-title mt-3">
              {{ $t("decaissement.capexSectionTitle") }}
            </div>
            <div class="section-hint text-muted mb-2">
              {{ $t("decaissement.capexJulyJuneHint") }}
            </div>
            <div class="row">
              <div class="col-md-6">
                <a-form-item
                  :label="$t('decaissement.capexDepreciationPeriodMonths')"
                >
                  <a-input-number
                    v-decorator="[
                      'capexDepreciationPeriodMonths',
                      {
                        rules: [
                          { required: true, message: $t('decaissement.validation.capexPeriodRequired') },
                        ],
                      },
                    ]"
                    :min="1"
                    :max="1200"
                    :precision="0"
                    style="width: 100%"
                    @change="bumpFormTick"
                  />
                </a-form-item>
              </div>
              <div class="col-md-6">
                <a-form-item :label="$t('decaissement.capexInvoiceDate')">
                  <a-date-picker
                    v-decorator="[
                      'capexInvoiceDate',
                      {
                        initialValue: moment(),
                        rules: [
                          { required: true, message: $t('decaissement.validation.capexInvoiceDateRequired') },
                        ],
                      },
                    ]"
                    style="width: 100%"
                    format="DD/MM/YYYY"
                    @change="onCapexInvoiceDateChange"
                  />
                </a-form-item>
              </div>
            </div>
            <div v-if="capexPreviewRows.length" class="row">
              <div class="col-md-12 mb-2">
                <div class="section-hint text-muted mb-2">
                  {{ $t("decaissement.capexSchedulePreview") }}
                </div>
                <a-table
                  :data-source="capexPreviewRows"
                  :pagination="false"
                  size="small"
                  row-key="schoolYearLabel"
                >
                  <a-table-column
                    :title="$t('decaissement.capexSchoolYearColumn')"
                    data-index="schoolYearLabel"
                    key="dsy"
                  />
                  <a-table-column
                    :title="$t('decaissement.capexMonthsColumn')"
                    data-index="months"
                    key="dmo"
                  />
                  <a-table-column
                    :title="$t('decaissement.capexAmountColumn')"
                    data-index="amount"
                    key="damt"
                  >
                    <template slot-scope="text">
                      <span class="amount-cell">{{
                        text != null ? Number(text).toFixed(3) : "—"
                      }}</span>
                    </template>
                  </a-table-column>
                </a-table>
              </div>
            </div>
          </template>

        </template>

        <template v-else>
          <div class="subsection-title">
            2.2 {{ $t("decaissement.formSubsectionAmount") }}
          </div>
          <div class="row">
            <div class="col-md-6">
              <a-form-item :label="$t('recette.montant')" :required="true">
                <a-input-number
                  v-decorator="[
                    'amount',
                    {
                      rules: [declareeAmountRule],
                    },
                  ]"
                  :min="0"
                  style="width: 100%"
                  @change="bumpFormTick"
                />
              </a-form-item>
            </div>
          </div>
        </template>
      </template>

      <div class="row provider-classroom-activity-row align-items-start">
        <div class="col-md-6">
          <classroom-allocation-editor
            v-model="classroomRows"
            :class-rooms="lookups.classRooms || []"
            :required="false"
            :show-percent="showAllocationPercents"
            @structure-change="onAllocationStructureChange"
          />
        </div>
        <div class="col-md-6 provider-activity-col">
          <activity-allocation-editor
            v-model="activityRows"
            :activities="lookups.activities || []"
            :required="false"
            :show-percent="showAllocationPercents"
            @structure-change="onAllocationStructureChange"
          />
        </div>
      </div>
      <div
        v-if="selectedAllocationCount > 0"
        class="allocation-combined-sum small"
        :class="{ 'text-danger': combinedAllocationSumInvalid }"
      >
        {{ $t("decaissement.combinedAllocationPercentTotal") }}:
        <strong>{{ combinedAllocationSum.toFixed(2) }}%</strong>
        / 100%
        <span v-if="selectedAllocationCount === 1" class="text-muted">
          — {{ $t("decaissement.combinedAllocationSingleHint") }}
        </span>
      </div>
    </div>

    <div class="form-section">
      <div class="section-title">
        3. {{ $t("decaissement.formSectionAttachments") }}
      </div>
      <div class="row">
        <div class="col-md-12">
          <a-form-item :label="$t('decaissement.attachment')">
            <a-upload-dragger
              :multiple="false"
              :before-upload="beforeUpload"
              :file-list="fileList"
              @change="onUploadChange"
              @remove="onUploadRemove"
            >
              <p class="ant-upload-drag-icon">
                <a-icon type="paper-clip" />
              </p>
              <p class="ant-upload-text">
                {{ $t("etablissement.clickOrDragToUpload") }}
              </p>
            </a-upload-dragger>
            <div v-if="uploadProgress.value > 0">
              <a-progress :percent="uploadProgress.value" />
            </div>
          </a-form-item>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <a-form-item :label="$t('comptabilite.description')">
            <a-textarea
              v-decorator="['description', { initialValue: '' }]"
              :rows="3"
            />
          </a-form-item>
        </div>
      </div>
    </div>

    <div class="form-section">
      <div class="section-title">
        4. {{ $t("decaissement.formSectionPayment") }}
      </div>
      <p class="section-hint text-muted">
        {{ $t("decaissement.formSectionPaymentHint") }}
      </p>
      <a-alert
        v-if="selectedSubcategoryDirection === 'inverse'"
        type="warning"
        show-icon
        class="mb-3"
        :message="$t('decaissement.directionInverseHint')"
      />
      <a-radio-group
        v-model="paymentHandling"
        button-style="solid"
        class="mb-3 payment-mode-radio"
        @change="bumpFormTick"
      >
        <a-radio-button value="immediate">
          {{ $t("decaissement.payNow") }}
        </a-radio-button>
        <a-radio-button
          value="scheduled"
          :disabled="selectedSubcategoryDirection === 'inverse'"
        >
          {{ $t("decaissement.planPayment") }}
        </a-radio-button>
      </a-radio-group>

      <schedule-payment-form
        v-show="paymentHandling === 'scheduled'"
        :key="'sched-' + paymentFormKey"
        ref="schedulePayment"
        :total-amount="depenseTotalPreview"
        :beneficiary-type="previewBeneficiaryType"
        :loading="loading"
        hide-submit
      />
      <immediate-payment-form
        v-show="paymentHandling === 'immediate'"
        :key="'imm-' + paymentFormKey"
        ref="immediatePayment"
        :payment-sources="lookups.paymentSources || {}"
        :total-amount="depenseTotalPreview"
        :loading="loading"
        hide-submit
      />
    </div>

    <div v-if="selectedSubcategoryDirection === 'inverse'" class="form-section">
      <div class="section-title">
        5. {{ $t("decaissement.repaymentSchedule") }}
      </div>
      <p class="section-hint text-muted">
        {{ $t("decaissement.repaymentScheduleHint") }}
      </p>
      <schedule-payment-form
        :key="'inv-' + paymentFormKey"
        ref="inverseScheduleForm"
        :total-amount="depenseTotalPreview"
        :loading="loading"
        auto-advance-monthly-from-first
        hide-submit
      />
    </div>

    <div class="submit-row">
      <a-button type="primary" html-type="submit" :loading="loading">
        {{ $t("action.enregistrer") }}
      </a-button>
    </div>
  </a-form>
</template>

<script>
import moment from "moment";
import { mapState } from "vuex";
import { uploadFilesToS3 } from "@/services/uploadService";
import SchedulePaymentForm from "./SchedulePaymentForm.vue";
import ImmediatePaymentForm from "./ImmediatePaymentForm.vue";
import BeneficiaryTypeDisplay from "./BeneficiaryTypeDisplay.vue";
import ActivityAllocationEditor from "./ActivityAllocationEditor.vue";
import ClassroomAllocationEditor from "./ClassroomAllocationEditor.vue";
import { getLevels } from "@/config/LevelConstants";
import {
  buildSubcategoryGroups,
  categoryIdForSubcategory,
  createSubcategoryFilter,
} from "@/utilities/depense/depenseSubcategories";
import {
  expenseKindTagColor,
  expenseKindTagLabelKey,
} from "@/utilities/depense/expenseKindUi";
import { computeCapexAmortizationSchedule } from "@/utilities/depense/capexAmortization";

export default {
  name: "DepenseCreateForm",
  components: {
    SchedulePaymentForm,
    ImmediatePaymentForm,
    BeneficiaryTypeDisplay,
    ActivityAllocationEditor,
    ClassroomAllocationEditor,
  },
  props: {
    lookups: { type: Object, required: true },
    loading: { type: Boolean, default: false },
  },
  beforeMount() {
    this.form = this.$form.createForm(this);
  },
  data() {
    return {
      initialDateMoment: moment(),
      formTick: 0,
      fileList: [],
      fileToUpload: null,
      uploadProgress: { value: 0 },
      /** immediate | scheduled — mirrors payment section UI (not ant-form). */
      paymentHandling: "immediate",
      /** Bumps :key on nested payment forms after reset so internal state clears. */
      paymentFormKey: 0,
      /** Default amortization invoice date applied once per CAPEX flow. */
      capexInvoiceDateSeeded: false,
      /** { activity: id, percent }[] — percents sum to 100 when multiple activities. */
      activityRows: [{ activity: undefined, percent: 0 }],
      /** { classRoom: id, percent }[] — combined with activities must sum to 100 when any selected. */
      classroomRows: [{ classRoom: undefined, percent: 0 }],
      /** Forces applyRetention checkbox remount when provider changes. */
      retentionCheckboxKey: "",
    };
  },
  computed: {
    ...mapState(["settings", "user"]),
    sortedSchoolarYears() {
      const years = this.settings?.schoolarYears || [];
      return [...years].sort((a, b) => {
        const aStart = a.start ? new Date(a.start).getTime() : 0;
        const bStart = b.start ? new Date(b.start).getTime() : 0;
        return aStart - bStart;
      });
    },
    expenseMode() {
      this.formTick;
      return this.form.getFieldValue("expenseMode") || "normal";
    },
    beneficiaryType() {
      this.formTick;
      return this.form.getFieldValue("beneficiaryType") || "provider";
    },
    countryCode() {
      return this.activeBuilding?.countryCode || "TN";
    },
    activeBuilding() {
      return (this.user?.building || []).find(
        (b) => b.dbName === this.settings?.activeBuilding
      );
    },
    schoolType() {
      return this.activeBuilding?.type || [];
    },
    levelOptionsList() {
      const levels = getLevels(
        this.$t.bind(this),
        this.schoolType,
        this.countryCode
      );
      return Object.entries(levels)
        .map(([value, label]) => ({ value: Number(value), label }))
        .sort((a, b) => a.value - b.value);
    },
    subcategoryGroups() {
      this.formTick;
      return buildSubcategoryGroups(
        this.lookups.categories || [],
        this.lookups.subcategories || []
      );
    },
    subcategoryFilterOption() {
      return createSubcategoryFilter(
        this.lookups.categories || [],
        this.lookups.subcategories || []
      );
    },
    beneficiaryOptions() {
      this.formTick;
      // Match select initialValue "provider"; before the field is registered, getFieldValue can be undefined and would wrongly fall through to employees.
      const bt = this.form.getFieldValue("beneficiaryType") || "provider";
      if (bt === "provider") return this.lookups.providers || [];
      if (bt === "associate") return this.lookups.associates || [];
      if (bt === "company") return this.lookups.companies || [];
      return this.lookups.employees || [];
    },
    computedAmountAfterRetention() {
      this.formTick;
      const ttc = Number(this.form.getFieldValue("amountTTC") || 0);
      if (!Number.isFinite(ttc) || ttc <= 0) return 0;
      if (!this.isRetentionApplied) {
        return Number(ttc.toFixed(3));
      }
      const retenuePercent = this.providerRetentionPercent;
      if (!Number.isFinite(retenuePercent) || retenuePercent <= 0) {
        return Number(ttc.toFixed(3));
      }
      return Number((ttc - (ttc * retenuePercent) / 100).toFixed(3));
    },
    providerHasRetention() {
      this.formTick;
      return this.providerRetentionPercent > 0;
    },
    defaultApplyRetentionForProvider() {
      return this.getProviderRetentionPercent(this.retentionCheckboxKey) > 0;
    },
    isRetentionApplied() {
      this.formTick;
      if (!this.providerHasRetention) return false;
      return this.form.getFieldValue("applyRetention") !== false;
    },
    providerRetentionPercent() {
      this.formTick;
      return this.getProviderRetentionPercent(
        this.form.getFieldValue("beneficiaryId")
      );
    },
    beneficiaryAccountingClass() {
      this.formTick;
      const id = this.form.getFieldValue("beneficiaryId");
      if (!id) return "";
      const list = this.beneficiaryOptions || [];
      const found = list.find((b) => String(b._id) === String(id));
      return (found && found.accountingClass) || "";
    },
    normalAmountRule() {
      return this.buildAmountRule(
        "decaissement.validation.amountRequired",
        "decaissement.validation.amountMustBePositive"
      );
    },
    declareeAmountRule() {
      return this.buildAmountRule(
        "decaissement.validation.amountRequired",
        "decaissement.validation.amountMustBePositive"
      );
    },
    amountHTRule() {
      return this.buildAmountRule(
        "decaissement.validation.amountHTRequired",
        "decaissement.validation.amountHTMustBePositive"
      );
    },
    amountTTCRule() {
      return this.buildAmountRule(
        "decaissement.validation.amountTTCRequired",
        "decaissement.validation.amountTTCMustBePositive"
      );
    },
    invoiceDateRule() {
      return this.notFutureDateRule("decaissement.invoiceDateNotInFuture");
    },
    entryDateRule() {
      return this.notFutureDateRule("decaissement.entryDateNotInFuture");
    },
    amountTvaRule() {
      return {
        validator: (rule, value, callback) => {
          if (value === null || value === undefined) {
            callback(
              new Error(this.$t("decaissement.validation.amountTVARequired"))
            );
            return;
          }
          callback();
        },
      };
    },
    /** Total amount for payment / schedules (same rules as saved depense). */
    depenseTotalPreview() {
      this.formTick;
      const mode = this.form.getFieldValue("expenseMode");
      if (mode === "normal") {
        return Number(this.form.getFieldValue("amount") || 0);
      }
      const bt = this.form.getFieldValue("beneficiaryType") || "provider";
      if (bt === "provider") {
        return Number(this.computedAmountAfterRetention || 0);
      }
      return Number(this.form.getFieldValue("amount") || 0);
    },
    previewBeneficiaryType() {
      this.formTick;
      if (this.form.getFieldValue("expenseMode") !== "declaree") {
        return null;
      }
      return this.form.getFieldValue("beneficiaryType") || null;
    },
    selectedActivityCount() {
      return (this.activityRows || []).filter((r) => r.activity).length;
    },
    selectedClassroomCount() {
      return (this.classroomRows || []).filter((r) => r.classRoom).length;
    },
    selectedAllocationCount() {
      return this.selectedActivityCount + this.selectedClassroomCount;
    },
    showAllocationPercents() {
      return this.selectedAllocationCount > 1;
    },
    combinedAllocationSum() {
      const activitySum = (this.activityRows || [])
        .filter((r) => r.activity)
        .reduce((s, r) => s + Number(r.percent || 0), 0);
      const classroomSum = (this.classroomRows || [])
        .filter((r) => r.classRoom)
        .reduce((s, r) => s + Number(r.percent || 0), 0);
      return activitySum + classroomSum;
    },
    combinedAllocationSumInvalid() {
      if (this.selectedAllocationCount === 0) return false;
      return Math.abs(this.combinedAllocationSum - 100) > 0.02;
    },
    isDeclaredProviderBeneficiary() {
      this.formTick;
      if (this.form.getFieldValue("expenseMode") !== "declaree") return false;
      return (
        (this.form.getFieldValue("beneficiaryType") || "provider") ===
        "provider"
      );
    },
    selectedSubcategoryDirection() {
      this.formTick;
      const subCatId = this.form.getFieldValue("subCategory");
      if (!subCatId) return "normal";
      const subs = this.lookups.subcategories || [];
      const idStr = String(subCatId);
      const found = subs.find((s) => String(s._id) === idStr);
      return (found && found.direction) || "normal";
    },
    selectedCategoryExpenseKind() {
      this.formTick;
      const sid = this.form.getFieldValue("subCategory");
      if (!sid) return null;
      const cid = categoryIdForSubcategory(
        this.lookups.subcategories || [],
        sid
      );
      if (!cid) return null;
      const cat = (this.lookups.categories || []).find(
        (c) => String(c._id) === String(cid)
      );
      if (!cat || !cat.depenseType) return null;
      const dt = cat.depenseType;
      return typeof dt === "object" ? dt.expenseKind : null;
    },
    isCapexExpense() {
      return this.selectedCategoryExpenseKind === "CAPEX";
    },
    /** Declared + provider beneficiary + CAPEX subcategory (category type). */
    showCapexEligibility() {
      this.formTick;
      if (!this.isCapexExpense) return false;
      if (this.form.getFieldValue("expenseMode") !== "declaree") return false;
      return (
        (this.form.getFieldValue("beneficiaryType") || "provider") ===
        "provider"
      );
    },
    /** HT / TVA / TTC entered and payable amount after retention is positive. */
    providerCapexAmountsReady() {
      this.formTick;
      if (!this.showCapexEligibility) return false;
      const ht = this.form.getFieldValue("amountHT");
      const tva = this.form.getFieldValue("amountTVA");
      const ttc = this.form.getFieldValue("amountTTC");
      if (ht === null || ht === undefined || ht === "") return false;
      if (tva === null || tva === undefined || tva === "") return false;
      if (ttc === null || ttc === undefined || ttc === "") return false;
      if (!(Number(ht) > 0) || !(Number(ttc) > 0)) return false;
      const M = Number(this.computedAmountAfterRetention || 0);
      return Number.isFinite(M) && M > 0;
    },
    /** Show CAPEX fields + schedule preview (after section 2.2 amounts). */
    showCapexAmortizationFormSection() {
      return this.showCapexEligibility && this.providerCapexAmountsReady;
    },
    capexPreviewRows() {
      this.formTick;
      if (!this.showCapexAmortizationFormSection) return [];
      const mode = this.form.getFieldValue("expenseMode") || "normal";
      const M =
        mode === "normal"
          ? Number(this.form.getFieldValue("amount"))
          : this.form.getFieldValue("beneficiaryType") === "provider"
          ? Number(this.computedAmountAfterRetention || 0)
          : Number(this.form.getFieldValue("amount") || 0);
      const nma = Number(
        this.form.getFieldValue("capexDepreciationPeriodMonths")
      );
      const inv = this.form.getFieldValue("capexInvoiceDate");
      if (
        !Number.isFinite(M) ||
        M <= 0 ||
        !Number.isFinite(nma) ||
        nma < 1 ||
        !inv
      ) {
        return [];
      }
      const invoiceDate = inv && inv.toDate ? inv.toDate() : inv;
      const { rows } = computeCapexAmortizationSchedule({
        totalAmount: M,
        depreciationPeriodMonths: Math.floor(nma),
        invoiceDate,
      });
      return rows;
    },
  },
  watch: {
    selectedSubcategoryDirection(dir) {
      if (dir === "inverse") {
        this.paymentHandling = "immediate";
      }
    },
    isCapexExpense(v) {
      if (!v) {
        this.capexInvoiceDateSeeded = false;
      }
    },
    showCapexAmortizationFormSection(v) {
      if (v && !this.capexInvoiceDateSeeded) {
        this.$nextTick(() => {
          this.form.setFieldsValue({ capexInvoiceDate: moment() });
          this.capexInvoiceDateSeeded = true;
          this.bumpFormTick();
        });
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      const y = this.settings?.activeSchoolarYear;
      if (y) {
        this.form.setFieldsValue({ schoolarYear: y });
      }
    });
  },
  methods: {
    expenseKindTagColor,
    expenseKindTagLabelKey,
    moment,
    disableFutureDates(current) {
      return current && current > moment().endOf("day");
    },
    notFutureDateRule(messageKey) {
      return {
        validator: (rule, value, callback) => {
          if (!value) {
            callback();
            return;
          }
          const d = value.toDate ? value.toDate() : value;
          if (moment(d).startOf("day").isAfter(moment().startOf("day"))) {
            callback(new Error(this.$t(messageKey)));
            return;
          }
          callback();
        },
      };
    },
    parseRetentionPercent(raw) {
      if (raw == null || raw === "") return 0;
      if (typeof raw === "number") {
        return Number.isFinite(raw) ? raw : 0;
      }
      const s = String(raw).trim();
      const digits = s.replace(/[^\d.,-]/g, "").replace(",", ".");
      const n = Number(digits);
      return Number.isFinite(n) ? n : 0;
    },
    formatRetentionPercent(raw) {
      const n = this.parseRetentionPercent(raw);
      if (!Number.isFinite(n)) return 0;
      return Number.isInteger(n) ? n : Number(n.toFixed(2));
    },
    bumpFormTick() {
      this.formTick += 1;
    },
    onCapexInvoiceDateChange() {
      this.bumpFormTick();
    },
    resetForm() {
      this.form.resetFields();
      this.formTick += 1;
      this.initialDateMoment = moment();
      this.form.setFieldsValue({
        expenseMode: "normal",
        date: moment(),
        entryDate: moment(),
        beneficiaryType: "provider",
        reference: "",
        description: "",
        capexDepreciationPeriodMonths: undefined,
        capexInvoiceDate: undefined,
        levels: [],
      });
      this.capexInvoiceDateSeeded = false;
      const y = this.settings?.activeSchoolarYear;
      if (y) {
        this.form.setFieldsValue({ schoolarYear: y });
      }
      this.fileList = [];
      this.fileToUpload = null;
      this.uploadProgress = { value: 0 };
      this.paymentHandling = "immediate";
      this.paymentFormKey += 1;
      this.resetActivityRows();
      this.resetClassroomRows();
    },
    resetActivityRows() {
      this.activityRows = [{ activity: undefined, percent: 0 }];
    },
    resetClassroomRows() {
      this.classroomRows = [{ classRoom: undefined, percent: 0 }];
    },
    /**
     * Equal split across all selected classrooms + activities (combined total = 100%).
     * Called when a selection is added/removed/changed, not when the user edits percents manually.
     */
    onAllocationStructureChange() {
      this.$nextTick(() => {
        this.redistributeAllocations();
      });
    },
    redistributeAllocations() {
      const activitySelected = (this.activityRows || []).filter((r) => r.activity);
      const classroomSelected = (this.classroomRows || []).filter((r) => r.classRoom);
      const n = activitySelected.length + classroomSelected.length;

      const nextActivities = (this.activityRows || []).map((r) => ({
        ...r,
        percent: r.activity ? 0 : 0,
      }));
      const nextClassrooms = (this.classroomRows || []).map((r) => ({
        ...r,
        percent: r.classRoom ? 0 : 0,
      }));

      if (n === 0) {
        this.activityRows = nextActivities;
        this.classroomRows = nextClassrooms;
        return;
      }

      const base = Math.floor((100 / n) * 100) / 100;
      let allocated = 0;
      let remaining = n;

      const assign = (rows, key) => {
        for (let i = 0; i < rows.length; i += 1) {
          if (!rows[i][key]) {
            rows[i].percent = 0;
            continue;
          }
          remaining -= 1;
          const pct =
            remaining === 0
              ? Math.round((100 - allocated) * 100) / 100
              : base;
          rows[i].percent = pct;
          allocated = Math.round((allocated + pct) * 100) / 100;
        }
      };

      assign(nextClassrooms, "classRoom");
      assign(nextActivities, "activity");
      this.classroomRows = nextClassrooms;
      this.activityRows = nextActivities;
    },
    onExpenseModeChange() {
      const mode = this.form.getFieldValue("expenseMode");
      this.formTick += 1;
      this.capexInvoiceDateSeeded = false;
      this.resetActivityRows();
      this.resetClassroomRows();
      if (mode === "declaree") {
        this.form.setFieldsValue({
          subCategory: undefined,
          amount: undefined,
          beneficiaryType: "provider",
          beneficiaryId: undefined,
          levels: [],
        });
      } else {
        this.form.setFieldsValue({
          beneficiaryType: "provider",
          beneficiaryId: undefined,
          invoiceNumber: undefined,
          amountHT: undefined,
          amountTTC: undefined,
          amountTVA: undefined,
          levels: [],
        });
      }
    },
    onBeneficiaryTypeChange() {
      this.formTick += 1;
      this.capexInvoiceDateSeeded = false;
      this.form.setFieldsValue({
        beneficiaryId: undefined,
        levels: [],
        applyRetention: false,
      });
      this.retentionCheckboxKey = "";
      this.resetActivityRows();
      this.resetClassroomRows();
    },
    onBeneficiaryIdChange(providerId) {
      const hasRetention = this.getProviderRetentionPercent(providerId) > 0;
      this.retentionCheckboxKey = String(providerId || "");
      this.formTick += 1;
      this.$nextTick(() => {
        this.form.setFieldsValue({ applyRetention: hasRetention });
        this.bumpFormTick();
      });
    },
    getProviderRetentionPercent(providerId) {
      if (!providerId) return 0;
      const providers = this.lookups.providers || [];
      const provider = providers.find(
        (p) => String(p._id) === String(providerId)
      );
      return this.parseRetentionPercent(provider && provider.retenue);
    },
    syncApplyRetentionDefault(providerId) {
      const id =
        providerId != null
          ? providerId
          : this.form.getFieldValue("beneficiaryId");
      const hasRetention = this.getProviderRetentionPercent(id) > 0;
      this.form.setFieldsValue({ applyRetention: hasRetention });
    },
    onProviderAmountPartChange(htFromEvent, tvaFromEvent) {
      const apply = () => {
        const htRaw =
          htFromEvent !== undefined
            ? htFromEvent
            : this.form.getFieldValue("amountHT");
        const tvaRaw =
          tvaFromEvent !== undefined
            ? tvaFromEvent
            : this.form.getFieldValue("amountTVA");
        const ht = Number(htRaw);
        const tva = Number(tvaRaw);
        if (!Number.isFinite(ht) || !Number.isFinite(tva)) {
          this.bumpFormTick();
          return;
        }
        this.form.setFieldsValue({
          amountTTC: Number((ht + tva).toFixed(3)),
        });
        this.bumpFormTick();
      };
      // `a-input-number` @change can fire before decorated field value syncs; prefer event value, then nextTick fallback.
      this.$nextTick(apply);
    },
    beneficiaryLabel(item) {
      if (item.fullName) return item.fullName;
      if (item.name) return item.name;
      return `${item.lastName || ""} ${item.firstName || ""}`.trim();
    },
    beforeUpload(file) {
      this.fileToUpload = file;
      this.fileList = [{ ...file, status: "done" }];
      this.uploadProgress = { value: 0 };
      return false;
    },
    onUploadChange({ fileList }) {
      const latest = (fileList || []).slice(-1);
      this.fileList = latest;
      const picked = latest[0];
      this.fileToUpload = picked ? picked.originFileObj || picked : null;
    },
    onUploadRemove() {
      this.fileToUpload = null;
      this.fileList = [];
      this.uploadProgress = { value: 0 };
      return true;
    },
    buildAmountRule(requiredKey, positiveKey) {
      return {
        validator: (rule, value, callback) => {
          if (value === null || value === undefined || value === "") {
            callback(new Error(this.$t(requiredKey)));
            return;
          }
          if (Number(value) <= 0) {
            callback(new Error(this.$t(positiveKey)));
            return;
          }
          callback();
        },
      };
    },
    showFirstValidationError(err) {
      const fields = Object.keys(err || {});
      for (let i = 0; i < fields.length; i += 1) {
        const errors = err[fields[i]]?.errors;
        if (errors && errors.length) {
          this.$message.error(errors[0].message);
          return;
        }
      }
      this.$message.error(
        this.$t("decaissement.validation.checkHighlightedFields")
      );
    },
    fieldsToValidate() {
      const base = ["expenseMode", "label", "date", "entryDate"];
      const mode = this.form.getFieldValue("expenseMode");
      const capexFields = this.showCapexAmortizationFormSection
        ? ["capexDepreciationPeriodMonths", "capexInvoiceDate"]
        : [];
      if (mode === "normal") {
        return [...base, "subCategory", "amount", ...capexFields];
      }
      const declareeBase = [
        ...base,
        "beneficiaryType",
        "beneficiaryId",
        "subCategory",
      ];
      const bt = this.form.getFieldValue("beneficiaryType");
      if (bt === "provider") {
        return [
          ...declareeBase,
          "invoiceNumber",
          "amountHT",
          "amountTTC",
          "amountTVA",
          ...capexFields,
        ];
      }
      return [...declareeBase, "amount", ...capexFields];
    },
    validateActivityRows() {
      const rows = this.activityRows.filter((r) => r.activity);
      const ids = rows.map((r) => r.activity);
      if (new Set(ids).size !== ids.length) {
        this.$message.error(this.$t("decaissement.activityDuplicate"));
        return false;
      }
      return true;
    },
    validateClassroomRows() {
      const rows = this.classroomRows.filter((r) => r.classRoom);
      const ids = rows.map((r) => r.classRoom);
      if (new Set(ids).size !== ids.length) {
        this.$message.error(this.$t("decaissement.classRoomDuplicate"));
        return false;
      }
      return true;
    },
    validateCombinedAllocations() {
      if (this.selectedAllocationCount === 0) {
        return true;
      }
      if (this.combinedAllocationSumInvalid) {
        this.$message.error(
          this.$t("decaissement.combinedAllocationPercentSumMustBe100")
        );
        return false;
      }
      for (const r of this.activityRows.filter((x) => x.activity)) {
        const p = Number(r.percent);
        if (!Number.isFinite(p) || p <= 0 || p > 100) {
          this.$message.error(
            this.$t("decaissement.combinedAllocationPercentSumMustBe100")
          );
          return false;
        }
      }
      for (const r of this.classroomRows.filter((x) => x.classRoom)) {
        const p = Number(r.percent);
        if (!Number.isFinite(p) || p <= 0 || p > 100) {
          this.$message.error(
            this.$t("decaissement.combinedAllocationPercentSumMustBe100")
          );
          return false;
        }
      }
      return true;
    },
    buildActivityAllocationsPayload() {
      return this.activityRows
        .filter((r) => r.activity)
        .map((r) => ({
          activity: r.activity,
          percent: Math.round(Number(r.percent) * 100) / 100,
        }));
    },
    buildClassRoomAllocationsPayload() {
      return this.classroomRows
        .filter((r) => r.classRoom)
        .map((r) => ({
          classRoom: r.classRoom,
          percent: Math.round(Number(r.percent) * 100) / 100,
        }));
    },
    /** Sends only fields expected by the API for the current expense mode (avoids stale keys when switching modes). */
    buildPayload(values, attachmentKeys) {
      const dateVal = values.date;
      const date = dateVal && dateVal.toDate ? dateVal.toDate() : dateVal;
      const entryDateVal = values.entryDate;
      const entryDate =
        entryDateVal && entryDateVal.toDate
          ? entryDateVal.toDate()
          : entryDateVal;
      const base = {
        expenseMode: values.expenseMode,
        label: values.label,
        reference: values.reference || "",
        ...(values.unit ? { unit: values.unit } : {}),
        schoolarYear: values.schoolarYear,
        date,
        entryDate,
        description: values.description || "",
        attachmentKeys,
      };
      if (values.expenseMode === "normal") {
        const subCat = values.subCategory;
        const category = categoryIdForSubcategory(
          this.lookups.subcategories || [],
          subCat
        );
        return {
          ...base,
          category,
          subCategory: subCat,
          amount: values.amount,
          ...(this.buildClassRoomAllocationsPayload().length
            ? { classRoomAllocations: this.buildClassRoomAllocationsPayload() }
            : {}),
          ...(this.buildActivityAllocationsPayload().length
            ? { activityAllocations: this.buildActivityAllocationsPayload() }
            : {}),
        };
      }
      if (values.beneficiaryType === "provider") {
        const subCat = values.subCategory;
        const category = categoryIdForSubcategory(
          this.lookups.subcategories || [],
          subCat
        );
        return {
          ...base,
          expenseMode: "declaree",
          category,
          subCategory: subCat,
          beneficiaryType: "provider",
          beneficiaryId: values.beneficiaryId,
          invoiceNumber: values.invoiceNumber,
          amountHT: values.amountHT,
          amountTTC: values.amountTTC,
          amountTVA: values.amountTVA,
          applyRetention: this.isRetentionApplied,
          ...(values.levels && values.levels.length
            ? { levels: values.levels.map((l) => Number(l)) }
            : {}),
          ...(this.buildClassRoomAllocationsPayload().length
            ? { classRoomAllocations: this.buildClassRoomAllocationsPayload() }
            : {}),
          ...(this.buildActivityAllocationsPayload().length
            ? { activityAllocations: this.buildActivityAllocationsPayload() }
            : {}),
          ...(this.showCapexAmortizationFormSection
            ? {
                capexDepreciation: {
                  invoiceDate:
                    values.capexInvoiceDate && values.capexInvoiceDate.toDate
                      ? values.capexInvoiceDate.toDate()
                      : values.capexInvoiceDate,
                  depreciationPeriodMonths: Math.floor(
                    Number(values.capexDepreciationPeriodMonths)
                  ),
                },
              }
            : {}),
        };
      }
      const subCatAe = values.subCategory;
      const categoryAe = categoryIdForSubcategory(
        this.lookups.subcategories || [],
        subCatAe
      );
      return {
        ...base,
        expenseMode: "declaree",
        category: categoryAe,
        subCategory: subCatAe,
        beneficiaryType: values.beneficiaryType,
        beneficiaryId: values.beneficiaryId,
        amount: values.amount,
        ...(this.buildClassRoomAllocationsPayload().length
          ? { classRoomAllocations: this.buildClassRoomAllocationsPayload() }
          : {}),
        ...(this.buildActivityAllocationsPayload().length
          ? { activityAllocations: this.buildActivityAllocationsPayload() }
          : {}),
      };
    },
    async submit() {
      const names = this.fieldsToValidate();
      this.form.validateFieldsAndScroll(names, async (err, values) => {
        if (err) {
          this.showFirstValidationError(err);
          return;
        }
        if (!this.validateActivityRows()) {
          return;
        }
        if (!this.validateClassroomRows()) {
          return;
        }
        if (!this.validateCombinedAllocations()) {
          return;
        }
        const total = this.depenseTotalPreview;
        if (!total || total <= 0) {
          this.$message.error(this.$t("decaissement.paymentTotalInvalid"));
          return;
        }
        let paymentPayload = null;
        if (this.paymentHandling === "scheduled") {
          paymentPayload = this.$refs.schedulePayment?.buildPayload?.();
        } else {
          paymentPayload = this.$refs.immediatePayment?.buildPayload?.();
        }
        if (!paymentPayload) {
          return;
        }
        let inverseSchedulesPayload = null;
        if (this.selectedSubcategoryDirection === "inverse") {
          inverseSchedulesPayload =
            this.$refs.inverseScheduleForm?.buildPayload?.();
          if (!inverseSchedulesPayload) {
            return;
          }
        }
        try {
          let attachmentKeys = [];
          if (this.fileToUpload) {
            attachmentKeys = await uploadFilesToS3(
              [this.fileToUpload],
              "decaissement/depense",
              this.uploadProgress
            );
          }
          const depensePayload = this.buildPayload(values, attachmentKeys);
          this.$emit(
            "submit",
            { depensePayload, paymentPayload, inverseSchedulesPayload },
            this.resetForm
          );
        } catch (e) {
          this.$message.error(this.$t("error.impoTelecharger"));
        }
      });
    },
  },
};
</script>
<style scoped>
.form-section {
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 16px 16px 4px;
  margin-bottom: 16px;
  background: #fff;
}

.section-title {
  font-weight: 600;
  margin-bottom: 12px;
  font-size: 14px;
}

.subsection-title {
  font-weight: 500;
  margin: 4px 0 10px;
  color: #595959;
}

.submit-row {
  display: flex;
  justify-content: flex-end;
}

.section-hint {
  font-size: 13px;
  margin: -4px 0 12px;
}

/* Solid radio buttons: single segmented control (no gap between buttons) */
.payment-mode-radio {
  display: inline-flex;
  flex-wrap: wrap;
}

.provider-classroom-activity-row {
  margin-bottom: 8px;
}

.provider-activity-col {
  border-left: 1px solid #e8e8e8;
  padding-left: 16px;
}

.allocation-combined-sum {
  margin-top: 4px;
  margin-bottom: 8px;
  color: #595959;
}
</style>

<style>
@import "~@/views/compV3/common.css";
</style>
